/**
 * `<BillingModal>` — the visit's billing editor, self-contained: give it an encounter and
 * it reads the Claim's lines and the note's AI suggestion, quotes the codes for the
 * rendering provider, and writes the result back through the one billing endpoint.
 * Mounted by the note's billing summary (the pencil) and by the Billing page.
 *
 * Place of service and duration have no home on a Claim, so they ride along on the note's
 * `ai-billing-suggestion` extension: the suggestion is sent back with the doctor's picks
 * folded in (selected candidate, add-ons, modifiers, POS, duration) and the AI's ranked
 * alternatives and rationale preserved. An encounter with no progress note gets an
 * explanatory stop instead of the editor (the write would 404 on it anyway).
 */
import { useEffect, useState } from 'react'
import { Button, Loader, Modal, Stack, Text } from '@mantine/core'
import { BillingEditModal, type BillingEntry } from './BillingEditModal'
import { removeCandidate, useVisitBilling, type VisitBillingSuggestion } from './useVisitBilling'

export interface BillingModalProps {
  encounterId: string
  opened: boolean
  onClose: () => void
  /** Runs after a successful save, before the modal closes. */
  onSaved?: () => Promise<void> | void
}

export function BillingModal({ encounterId, opened, onClose, onSaved }: BillingModalProps) {
  // Codes picked in the editor but not saved yet, so the quote covers them too (PD-1647).
  const [pickedCodes, setPickedCodes] = useState<string[]>([])
  const billing = useVisitBilling(encounterId, pickedCodes)

  // A candidate removed inside the editor is gone from the suggestion Save writes, and
  // from nothing else until then; closing without saving forgets the removal.
  const [editedSuggestion, setEditedSuggestion] = useState<VisitBillingSuggestion>()
  useEffect(() => {
    if (!opened) setEditedSuggestion(undefined)
  }, [opened])
  const suggestion = editedSuggestion ?? billing.suggestion

  const handleDeleteCandidate = (rank: number) => {
    if (suggestion) setEditedSuggestion(removeCandidate(suggestion, rank))
  }

  // The server stamps who confirmed and when on Save; this only marks the code.
  const handleConfirmDiagnosis = (code: string) => {
    if (!suggestion?.diagnoses) return
    const confirmedAt = new Date().toISOString()
    setEditedSuggestion({
      ...suggestion,
      diagnoses: suggestion.diagnoses.map((dx) => (dx.code === code ? { ...dx, confirmedAt } : dx)),
    })
  }

  const handleSave = async ({
    entries,
    placeOfService,
    durationMinutes,
  }: {
    entries: BillingEntry[]
    placeOfService?: string
    durationMinutes?: number
  }) => {
    const nextSuggestion = entriesToSuggestion(entries, suggestion, {
      placeOfService,
      durationMinutes,
    })

    // The error notification comes from the save; a failure keeps the editor open with
    // the doctor's edits intact.
    try {
      await billing.save({ entries, suggestion: nextSuggestion })
    } catch {
      return
    }

    await onSaved?.()
    onClose()
  }

  if (!opened) return null

  if (billing.isPending) {
    return (
      <Modal opened onClose={onClose} title="Billing Codes" centered radius="lg">
        <div className="flex justify-center p-6">
          <Loader size="sm" />
        </div>
      </Modal>
    )
  }

  // Never an empty editor on a failed read: saving it would rewrite the visit's Claim
  // with no lines (the server only skips a save when the visit has no Claim at all).
  if (billing.isError || !billing.data) {
    return (
      <Modal opened onClose={onClose} title="Billing Codes" centered radius="lg">
        <Stack gap="sm" className="p-2">
          <Text size="sm" c="dimmed">
            The billing for this visit could not be loaded. Nothing was changed.
          </Text>
          <Button
            variant="light"
            size="xs"
            className="self-start"
            onClick={() => void billing.refetch()}
          >
            Try again
          </Button>
        </Stack>
      </Modal>
    )
  }

  if (billing.data.documentReferenceId === null) {
    return (
      <Modal opened onClose={onClose} title="Billing Codes" centered radius="lg">
        <Text size="sm" c="dimmed" className="p-2">
          This encounter has no progress note yet. Billing codes are added to the visit note —
          create the progress note first, then add codes here or from the note itself.
        </Text>
      </Modal>
    )
  }

  return (
    <BillingEditModal
      opened
      onClose={() => (!billing.isSaving ? onClose() : undefined)}
      patientId={billing.data.patientId}
      initialEntries={billing.entries}
      diagnosesFromNote={billing.data.diagnosesFromNote}
      diagnosesUnavailable={billing.data.diagnosesUnavailable}
      dateOfService={billing.data.dateOfService}
      initialPlaceOfService={suggestion?.placeOfService}
      initialDurationMinutes={suggestion?.durationMinutes}
      aiReasoning={suggestion?.reasoning}
      aiCandidates={suggestion?.candidates}
      aiAddOnCodes={suggestion?.addOnCodes}
      aiDiagnoses={suggestion?.diagnoses}
      onConfirmDiagnosis={handleConfirmDiagnosis}
      isSaving={billing.isSaving}
      chargeQuote={billing.quote.quote}
      priceFor={billing.quote.priceFor}
      onCodesChange={setPickedCodes}
      isQuoting={billing.quote.isQuoting}
      quoteError={billing.quote.quoteError}
      providerMissing={billing.quote.providerMissing}
      providerRemoved={billing.quote.providerRemoved}
      providerName={billing.quote.providerDisplay}
      onDeleteCandidate={handleDeleteCandidate}
      onSave={handleSave}
    />
  )
}

/**
 * The suggestion to persist after the editor saves: the first line is the primary, the
 * rest are add-ons. When the primary is one of the AI's candidates the full candidate
 * list is kept and `selectedRank` moves to it; a code outside the AI's suggestions is
 * prepended as the new rank-1 candidate with the AI's shifted down, so the chips still
 * offer the AI's options as switchable alternatives.
 */
function entriesToSuggestion(
  entries: BillingEntry[],
  prev: VisitBillingSuggestion | undefined,
  overrides: { placeOfService?: string; durationMinutes?: number },
): VisitBillingSuggestion {
  const placeOfService = overrides.placeOfService ?? prev?.placeOfService ?? ''
  const durationMinutes = overrides.durationMinutes ?? prev?.durationMinutes ?? 0
  const reasoning = prev?.reasoning ?? ''
  // The AI's quotes, review reasons and confirmations ride along untouched (PD-2018).
  const diagnoses = prev?.diagnoses ? { diagnoses: prev.diagnoses } : {}

  if (!entries.length) {
    return {
      candidates: [],
      selectedRank: 1,
      addOnCodes: [],
      modifiers: [],
      placeOfService,
      durationMinutes,
      reasoning,
      ...diagnoses,
    }
  }

  const [primary, ...addOns] = entries
  const pickedCode = primary.procedure?.code ?? ''
  const pickedDisplay = primary.procedure?.display ?? ''
  const prevCandidates = prev?.candidates ?? []
  const matchedCandidate = prevCandidates.find((candidate) => candidate.code === pickedCode)

  const candidates = matchedCandidate
    ? prevCandidates
    : [
        { code: pickedCode, display: pickedDisplay, rank: 1, reasoning: '' },
        ...prevCandidates
          .filter((candidate) => candidate.code !== pickedCode)
          .map((candidate, index) => ({ ...candidate, rank: index + 2 })),
      ]

  return {
    candidates,
    selectedRank: matchedCandidate?.rank ?? 1,
    // The AI's per-add-on rationale survives when the pick maps back to a suggested
    // add-on; a manually added one carries none, and the editor hides its hover.
    addOnCodes: addOns.map((addOn) => {
      const code = addOn.procedure?.code ?? ''
      return {
        code,
        display: addOn.procedure?.display ?? '',
        reasoning: prev?.addOnCodes.find((p) => p.code === code)?.reasoning ?? '',
      }
    }),
    modifiers: (primary.modifiers || []).filter((modifier) => modifier && modifier.trim() !== ''),
    placeOfService,
    durationMinutes,
    reasoning,
    ...diagnoses,
  }
}
